import { DataView } from 'primereact/dataview';
import { useEffect, useState } from 'react';
import PageWrapper from '../../../../components/Display/PageWrapper';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useNavigationContext } from '../../../../contexts/NavigationContext';
import { privateFetch } from '../../../../utils/apiHelper';
import NewsDetail from './NewsDetail';
import NewsItem from './NewsItem';
import NewsManager from './NewsManager';
import DocumentPanel from '../../common/document/DocumentPanel';
import { CONSTANT } from '../../../../utils/constants';
import { Dropdown } from 'primereact/dropdown';

function NewsPage({ isActive }) {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [newsDetail, setNewsDetail] = useState(undefined);
  const [newsList, setNewslist] = useState([]);
  const [loading, setLoading] = useState(false);
  const { accountContext } = useAccountContext();
  const { openQBNewsDetail, navigationContext } = useNavigationContext();
  const [duplicationMode, setDuplicationMode] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(undefined);
  const [documentManagerIsOpen, setDocumentManagerIsOpen] = useState(false);
  const [thematicFilter, setThematicFilter] = useState(undefined);

  useEffect(() => {
    fetchNewsList();
  }, [navigationContext, thematicFilter]);

  const fetchNewsList = async () => {
    await privateFetch(
      'GET',
      'quatrebis',
      `/cu/${accountContext.id}/news/?thematic=${thematicFilter || ''}`
    ).then((res) => {
      if (res) setNewslist(res);
    });
  };

  const fetchNewsDetail = async () => {
    await privateFetch(
      'GET',
      'resources',
      `/cu/${accountContext.id}/news/${navigationContext.acitveNewsId}/`
    ).then((res) => {
      if (res) setNewsDetail(res);
    });
  };

  const createNews = async (newsForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'quatrebis',
      `/cu/${accountContext.id}/news/`,
      "L'article a bien été ajouté",
      JSON.stringify(newsForm)
    ).then(() => {
      fetchNewsList();
      setLoading(false);
      setPanelIsOpen(false);
      setNewsDetail(undefined);
      setDuplicationMode(false);
    });
  };

  const updateNews = async (newsForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'resources',
      `/cu/${accountContext.id}/news/${navigationContext.acitveNewsId}/`,
      "L'article a bien été modifié",
      JSON.stringify(newsForm)
    ).then(() => {
      fetchNewsList();
      fetchNewsDetail();
      setLoading(false);
      setPanelIsOpen(false);
    });
  };

  const deleteNews = async (news) => {
    await privateFetch(
      'DELETE',
      'resources',
      `/cu/${accountContext.id}/news/${news.id}/`,
      "L'article a bien été supprimé"
    ).then(() => {
      fetchNewsList();
      setNewsDetail(undefined);
    });
  };

  const handleClickNews = (news) => {
    openQBNewsDetail(news);
  };

  useEffect(() => {
    if (navigationContext.acitveNewsId) {
      fetchNewsDetail();
    } else setNewsDetail(undefined);
  }, [navigationContext]);

  const goBackNewsAction = {
    onClick: handleClickNews,
    label: 'Retour à la liste',
    icon: 'pi pi-arrow-left'
  };

  const handleOpenManager = () => {
    setPanelIsOpen(true);
  };

  const newNewsAction = {
    onClick: handleOpenManager,
    label: 'Ajouter une actualité',
    icon: 'pi-plus'
  };

  const handleDuplicateNews = (news) => {
    handleOpenManager();
    setDuplicationMode(true);
    handleClickNews(news);
  };

  const handleCreateDocument = () => {
    setSelectedDocument(undefined);
    setDocumentManagerIsOpen(true);
  };

  const handleUpdateDocument = (document) => {
    setSelectedDocument(document);
    setDocumentManagerIsOpen(true);
  };

  const uploadNewsDocument = async (documentForm) => {
    setLoading(true);
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/news/${newsDetail.id}/file/`,
      'Le fichier a bien été enregistrée',
      documentForm,
      false,
      true
    ).then(() => {
      fetchNewsDetail();
      setLoading(false);
      setDocumentManagerIsOpen(false);
    });
  };

  const updateDocument = async (documentForm) => {
    setLoading(true);
    await privateFetch(
      'PUT',
      'resources',
      `/cu/${accountContext.id}/file/${selectedDocument.id}/`,
      'Le document a bien été modifiée',
      documentForm,
      false,
      true
    ).then(() => {
      fetchNewsDetail();
      setLoading(false);
      setDocumentManagerIsOpen(false);
    });
  };

  const handleDeleteDocument = async (document) => {
    await privateFetch(
      'DELETE',
      'resources',
      `/cu/${accountContext.id}/file/${document.id}/`,
      'Le document a bien été supprimée'
    ).then(() => {
      fetchNewsDetail();
      setSelectedDocument(undefined);
    });
  };

  const itemTemplate = (news) => (
    <NewsItem
      news={news}
      handleClickNews={() => handleClickNews(news)}
      handleDuplicateNews={() => handleDuplicateNews(news)}
    />
  );

  const newsFilters = (
    <div className="flex flex-row flex-wrap gap-2 justify-start">
      <div className="flex flex-row flex-wrap gap-2 ">
        <div className="fit-content mb-4">
          <Dropdown
            value={thematicFilter}
            options={CONSTANT.quatrebis.thematique}
            optionLabel="label"
            onChange={(e) => setThematicFilter(e.value)}
            placeholder="Filtrer par Thématique"
            className="grow rounded-full pl-2"
            filter
            showClear
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={`h-full w-full flex-col ${isActive ? 'flex' : 'hidden'}`}>
      <PageWrapper
        label="Actualités"
        labelAction={handleClickNews}
        action={newsDetail ? goBackNewsAction : newNewsAction}
        detail={newsDetail}>
        {newsDetail && !duplicationMode ? (
          <NewsDetail
            news={newsDetail}
            handleUpdateNews={handleOpenManager}
            handleDelete={deleteNews}
            handleCreateFile={handleCreateDocument}
            handleUpdateFile={handleUpdateDocument}
            handleDeleteFile={handleDeleteDocument}
          />
        ) : (
          <>
            {newsFilters}
            <DataView value={newsList} itemTemplate={itemTemplate} className="pb-20" />
          </>
        )}
      </PageWrapper>
      <NewsManager
        isOpen={panelIsOpen}
        onClose={() => {
          setDuplicationMode(false);
          setPanelIsOpen(false);
        }}
        news={newsDetail}
        onCreate={createNews}
        onUpdate={updateNews}
        loading={loading}
        duplicationMode={duplicationMode}
      />
      <DocumentPanel
        document={selectedDocument}
        loading={loading}
        isOpen={documentManagerIsOpen}
        onClose={() => {
          setSelectedDocument(null);
          setDocumentManagerIsOpen(false);
        }}
        onCreate={uploadNewsDocument}
        onUpdate={updateDocument}
      />
    </div>
  );
}

export default NewsPage;
